import { useState, useEffect } from "react";
import { Sidebar } from "primereact/sidebar";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";

import type {
  AssignmentsReportFilterOptions,
  AssignmentReportFilters,
} from "@/api/assignmentsReport";
import { ASSIGNMENT_STATUS_OPTIONS } from "@/constants/assignmentsReport.constants";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AssignmentsReportFiltersProps {
  visible: boolean;
  onHide: () => void;
  filterOptions: AssignmentsReportFilterOptions | null;
  loadingOptions: boolean;
  currentFilters: AssignmentReportFilters;
  onApply: (filters: AssignmentReportFilters) => void;
  onClear: () => void;
}

// ---------------------------------------------------------------------------
// FilterField — reusable labelled MultiSelect row
// ---------------------------------------------------------------------------

interface FilterFieldProps {
  placeholder: string;
  options: string[] | undefined;
  value: string[];
  onChange: (value: string[]) => void;
  loading: boolean;
}

function FilterField({ placeholder, options, value, onChange, loading }: FilterFieldProps) {
  return loading ? (
    <Skeleton width="100%" height="2.5rem" />
  ) : (
    <MultiSelect
      options={(options ?? []).map(v => ({ label: v, value: v }))}
      value={value}
      onChange={e => onChange(e.value as string[])}
      placeholder={placeholder}
      display="chip"
      maxSelectedLabels={2}
      filter
      className="w-full"
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AssignmentsReportFilters({
  visible,
  onHide,
  filterOptions,
  loadingOptions,
  currentFilters,
  onApply,
  onClear,
}: AssignmentsReportFiltersProps) {
  const [draft, setDraft] = useState<AssignmentReportFilters>(currentFilters);

  // Re-sync draft from latest persisted filters every time the sidebar opens
  useEffect(() => {
    if (visible) setDraft(currentFilters);
  }, [visible]);

  const setField = (field: keyof AssignmentReportFilters, value: string[]) =>
    setDraft(prev => ({ ...prev, [field]: { value } }));

  return (
    <Sidebar visible={visible} position="right" onHide={onHide} className="w-lg">
      <div className="flex flex-col space-y-10 pb-16">
        {/* Assignment Status */}
        <section className="flex flex-col space-y-4">
          <h3 className="atlas-h5">Assignment Status</h3>
          <MultiSelect
            options={ASSIGNMENT_STATUS_OPTIONS}
            value={draft.status?.value ?? []}
            onChange={e => setField("status", e.value as string[])}
            placeholder="Select status"
            display="chip"
            maxSelectedLabels={2}
            filter
            className="w-full"
          />
        </section>

        {/* User Information */}
        <section className="flex flex-col space-y-4">
          <h3 className="atlas-h5">User Information</h3>
          <FilterField
            placeholder="Select job roles"
            options={filterOptions?.jobRole}
            value={draft.jobRole?.value ?? []}
            onChange={v => setField("jobRole", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select titles"
            options={filterOptions?.title}
            value={draft.title?.value ?? []}
            onChange={v => setField("title", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select managers"
            options={filterOptions?.manager}
            value={draft.manager?.value ?? []}
            onChange={v => setField("manager", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select manager emails"
            options={filterOptions?.managersEmail}
            value={draft.managersEmail?.value ?? []}
            onChange={v => setField("managersEmail", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select employee numbers"
            options={filterOptions?.employeeNumber}
            value={draft.employeeNumber?.value ?? []}
            onChange={v => setField("employeeNumber", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select security champion"
            options={filterOptions?.securityChampion}
            value={draft.securityChampion?.value ?? []}
            onChange={v => setField("securityChampion", v)}
            loading={loadingOptions}
          />
        </section>

        {/* Organization */}
        <section className="flex flex-col space-y-4">
          <h3 className="atlas-h5">Organization</h3>
          <FilterField
            placeholder="Select departments"
            options={filterOptions?.department}
            value={draft.department?.value ?? []}
            onChange={v => setField("department", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select business units"
            options={filterOptions?.businessUnit}
            value={draft.businessUnit?.value ?? []}
            onChange={v => setField("businessUnit", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select teams"
            options={filterOptions?.team}
            value={draft.team?.value ?? []}
            onChange={v => setField("team", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select companies"
            options={filterOptions?.company}
            value={draft.company?.value ?? []}
            onChange={v => setField("company", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select countries"
            options={filterOptions?.country}
            value={draft.country?.value ?? []}
            onChange={v => setField("country", v)}
            loading={loadingOptions}
          />
        </section>

        {/* Assignments */}
        <section className="flex flex-col space-y-4">
          <h3 className="atlas-h5">Assignments</h3>
          <FilterField
            placeholder="Select assignments"
            options={filterOptions?.assignment}
            value={draft.assignment?.value ?? []}
            onChange={v => setField("assignment", v)}
            loading={loadingOptions}
          />
        </section>
      </div>

      <div className="flex gap-2 pt-3 border-t absolute bottom-4 left-4 right-4">
        <Button
          label="Clear All"
          severity="secondary"
          outlined
          onClick={onClear}
          className="flex-1"
        />
        <Button label="Cancel" severity="secondary" onClick={onHide} />
        <Button label="Apply" onClick={() => onApply(draft)} className="flex-1" />
      </div>
    </Sidebar>
  );
}
